import { reactive } from 'vue';
import { formatPhoneNumber, formatPostalCode } from '@/utils/dataUtils';
import { focusFirstInvalidInput } from '@/utils/pageUtils';
import useVuelidate from '@vuelidate/core';


/**
 * @deprecated This composable is deprecated and will be removed in future versions. Use formState instead.
 * @param {*} initialFormData 
 * @param {*} rules 
 * @returns {Object} Form state and utility functions
 */
export function useForm(initialFormData = {}, rules = {}) {
  console.warn('The useForm composable is deprecated and will be removed in future versions. Use formState instead.');
  const formData = reactive(initialFormData);
  const state = reactive({
    data: formData,
    loading: false,
    loadingMessage: 'Loading...',
    success: false,
    error: false,
    errorMessage: '',
    $v: useVuelidate(rules, formData, { $lazy: true, $autoDirty: true })
  });

  const setLoadingState = (message = 'Loading...') => {
    state.loading = true;
    state.loadingMessage = message;
    state.error = false;
    state.errorMessage = '';
    state.success = false;
  };

  const hideLoadingState = () => {
    state.loading = false;
  };

  const setErrorState = (error) => {
    let message = error.message;
    state.error = true;
    // Check if the error has a response (i.e., the server responded with a status code)
    if (error.response) {
      const statusCode = error.response.status;
      const errorMessage = error.response.data?.message || message;

      if (statusCode === 404) {
        // Handle 404 Not Found
        message = 'Document not found.';
      } else if (statusCode === 401) {
        // Handle 401 Unauthorized
        message = 'Unauthorized.';
      } else {
        // Handle other status codes
        message = `Error ${statusCode}: ${errorMessage}`;
      }
    } else if (error.request) {
      // The request was made but no response was received
      message = 'No response from server. Please check your network.';
    } 

    state.errorMessage = message;
  };

  const save = async (saveFunction) => {
    setLoadingState('Saving...');
    try {
      const result = await saveFunction();
      state.success = true;
      return result;
    } catch (error) {
      setErrorState(error);
    } finally {
      hideLoadingState();
    }
  };

  const saveWithMessage = async (saveFunction, message) => {
    setLoadingState(message);

    try {
      const result = await saveFunction();
      state.success = true;
      return result;
    } catch (error) {
      setErrorState(error);
    } finally {
      hideLoadingState();
    }
  };

  const fetch = async (fetchFunction) => {
    state.loading = true;
    state.success = false;
    state.error = false;
    try {
      await fetchFunction();
      state.success = true;
    } catch (error) {
      setErrorState(error);
    } finally {
      hideLoadingState();
    }
  };

  function sanitizeNumber($event) {
    return $event.replace(/\D/g, '');
};

const focusInvalidInput = () => {
  focusFirstInvalidInput();
};

const focusErrorMessage = (elementName) => {
    scrollIntoView(elementName);
};

const scrollIntoView = (elementName) => {
  const element = document.getElementById(elementName);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' });
  }
};

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const setFocus = (elementName) => {
  const element = document.getElementById(elementName);
  if (element) {
    element.focus();
    if (element.select) {
    element.select(); 
    }
  }
};

function getFormattedPhoneNumber(value) {
    return formatPhoneNumber(value);
}

function getFormattedPostalCode(value) {
    return formatPostalCode(value);
}

const isSavable = () => {
  if (state.$v.$anyDirty) {
    state.$v.$touch();
    return !state.$v.$error;
  } else {
    return state.$v.$error;
  }
};

  return {
    state,
    save,
    saveWithMessage,
    fetch,
    sanitizeNumber,
    getFormattedPostalCode,
    getFormattedPhoneNumber,
    focusInvalidInput,
    focusErrorMessage,
    scrollIntoView,
    scrollToTop,
    setFocus,
    isSavable
  };
}